import { useTranslation } from 'react-i18next';
import { Fx, Layout, Select, Table, Icon, Pagination } from 'Components';
import { GetSystemRolesRequest, SystemRole, SystemRoleType } from 'Services/Api/Roles/Types';
import { GET_ROLES_INITIAL_PARAMS, useRoles } from './Roles.hooks';
import { useMemo, useState, useEffect, useRef } from 'react';
import { getNamesBySelectedRowKeys } from './Roles.helpers';
import useColumns from './columns';
import './styles/Roles.module.scss';
import { GROUPS_LABEL } from 'Pages/Groups/Groups.constants';

import { getAppRoutes } from 'Pages/App/App.constants';
import { generatePath, useHistory } from 'react-router-dom';
import { useTenants } from 'Components/Sidebar/hooks/useTenants';
import RolesActions from './RolesActions';
import { useStores } from 'Hooks/useStore';
import DebounceInput from 'Components/DebouncedInput';

function Roles() {
	const { t } = useTranslation();
	const {
		refetch,
		dataSource,
		onTableChange,
		selectedRowKeys,
		selectedTenantId,
		onTenantChange,
		result,
		loading,
		page,
		pageSize,
		total,
		params,
		onPaginationChange,
		setRoleName
	} = useRoles();

	const { authStore, systemsStore } = useStores();
	const { loading: tenantLoading, } = useTenants();
	const { tenantList, selectedTenant } = systemsStore;

	const isSuperAdmin = authStore.currentUser?.primarySystemRole?.role === SystemRoleType.SuperAdmin;

	const selectedTenantValue = useMemo(() => {
		if (isSuperAdmin) {
			return selectedTenant?.value ?? 0;
		}
		return selectedTenant?.value || tenantList[0]?.value;
	}, [isSuperAdmin, selectedTenant, tenantList]);

	const tenantRef = useRef(selectedTenantValue);

	useEffect(() => {
		tenantRef.current = selectedTenantValue;
	}, [selectedTenantValue]);

	const TENANTS = useMemo(() => {
		if (!isSuperAdmin) {
			return tenantList;
		}

		return [
			{
				label: t(GROUPS_LABEL.ALL_TENANTS) as string,
				value: 0
			},
			...tenantList
		];
	}, [isSuperAdmin, tenantList, t]);

	const handleTenantChange = (id: number | string) => {
		const tenantId = Number(id);

		if (tenantId === 0 && isSuperAdmin) {
			systemsStore.changeTenant({
				label: t(GROUPS_LABEL.ALL_TENANTS) as string,
				value: 0
			});
			onTenantChange(0);
			return;
		}

		const tenant = tenantList.find(t => t.value === tenantId);
		if (!tenant) return;
		systemsStore.changeTenant(tenant);
		onTenantChange(tenant.value);
	};

	const namesBySelectedRowKeys = useMemo(
		() => getNamesBySelectedRowKeys(dataSource, selectedRowKeys),
		[dataSource, selectedRowKeys]
	);

	const history = useHistory();
	const columns = useColumns<SystemRole>({
		params,
		actions: {
			onViewDetail: (record) =>
				history.push(
					generatePath(getAppRoutes().VIEW_ROLE, {
						roleId: record.id
					})
				),

			onEdit: (record) =>
				history.push(
					generatePath(getAppRoutes().EDIT_ROLE, {
						roleId: record.id
					})
				),

			onClone: (record) =>
				history.push(
					generatePath(getAppRoutes().CLONE_ROLE, {
						roleId: record.id
					})
				),

			onRestoreToDefaults: (record) => {
				// restore logic.
			},
			onDelete: (record) => {
				// delete logic
			}
		}
	});

	// const onControlSubmit = () => {
	// 	setSelectedRowKeys([]);
	// 	handleSearchChange("");
	// };

	const handleSearchChange = (value: string) => {
		const variables: GetSystemRolesRequest = {
			sort: result.variables?.sort ?? GET_ROLES_INITIAL_PARAMS.sort,
			filters: {
				...result.variables?.filters,
				roleName: value
			}
		};
		setRoleName(value);
		refetch(variables, tenantRef?.current, 1, pageSize);
	};

	useEffect(() => {
		if (!!isSuperAdmin) {
			if (selectedTenantValue !== selectedTenantId) {
				onTenantChange(selectedTenantValue);
			}
			return;
		} else {
			if (!!selectedTenantValue) {
				onTenantChange(selectedTenantValue);
			}
		}
	}, [isSuperAdmin, selectedTenantValue]);

	useEffect(() => {
		if (
			!isSuperAdmin &&
			tenantList.length &&
			!selectedTenant?.value
		) {
			systemsStore.changeTenant(tenantList[0]);
		}
	}, [tenantList, isSuperAdmin, selectedTenant]);

	return (
		<Layout>
			<div className="role-header">
				<Fx align="middle" className="role-header-content" justify="space-between">
					<div>
						<h1>{t('roles.title')}</h1>
					</div>
					<div className="pm-dropdown-section">
						<Select
							id="pm-client-select"
							placeholder={t('phishingManager.selectTenant')}
							dropdownClassName="role-setup-filter-option-select phishing-tenant-dropdown"
							value={selectedTenantValue}
							onChange={handleTenantChange}
							showSearch
							loading={tenantLoading}
							disabled={!isSuperAdmin}
							optionFilterProp="label"
							suffixIcon={<Icon type="campaignDropdown" />}
							options={TENANTS}
							getPopupContainer={(trigger) => trigger.parentElement}
						/>
					</div>
				</Fx>
			</div>

			<div className='role-action-wrapper'>
				<div className="role-select-wrapper__filter-group role-select-wrapper__role-search">
					<div>
						<DebounceInput
							placeholder={'Search'}
							setDebouncedSearch={handleSearchChange}
						/>
					</div>
				</div>
				<RolesActions
					ids={selectedRowKeys as string[]}
					idsNames={namesBySelectedRowKeys}
					onSubmit={() => { console.log("submit") }}
					hasData={Boolean(dataSource.length)}
					params={result.variables}
				/>
			</div>
			<Table<SystemRole>
				dataSource={dataSource}
				rowKey="roleName"
				columns={columns}
				loading={loading}
				pagination={false}
				locale={{
					emptyText: !selectedTenantValue
						? "No Tenant Selected"
						: "",
				}}
				onChange={onTableChange}
				totalVisible
			/>
			<Fx shrink={0} style={{ paddingBottom: "55px" }}>
				<Pagination
					total={total}
					pageSize={pageSize}
					current={page}
					onChange={onPaginationChange}
				/>
			</Fx>
		</Layout>
	);
}

export default Roles;